import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  authTokens,
  setAuthFailureHandler,
  isRemoteApiEnabled,
  isInvalidSessionError,
} from '@/api/client'
import { STORAGE_KEYS } from '@/constants'
import { isStaffRole } from '@/components/admin/access'
import { loginUser, logoutUser, getCurrentUser } from '@/services/authService'

const AuthContext = createContext(null)

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.authUser)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function persistUser(user) {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.authUser, JSON.stringify(user))
  } else {
    localStorage.removeItem(STORAGE_KEYS.authUser)
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (authTokens.access ? readStoredUser() : null))
  const [isHydrating, setIsHydrating] = useState(
    () => isRemoteApiEnabled() && Boolean(authTokens.access)
  )
  const [sessionError, setSessionError] = useState(null)
  const [sessionAttempt, setSessionAttempt] = useState(0)
  const sessionCheck = useRef(0)

  const clear = useCallback(() => {
    sessionCheck.current += 1
    setUser(null)
    setSessionError(null)
    setIsHydrating(false)
    persistUser(null)
    authTokens.clear()
  }, [])

  useEffect(() => {
    setAuthFailureHandler(() => clear())
    return () => setAuthFailureHandler(null)
  }, [clear])

  useEffect(() => {
    const check = ++sessionCheck.current
    if (!isRemoteApiEnabled() || !authTokens.access) {
      return
    }
    getCurrentUser()
      .then((fresh) => {
        if (check !== sessionCheck.current) return
        setUser(fresh)
        persistUser(fresh)
      })
      .catch((error) => {
        if (check !== sessionCheck.current) return
        if (isInvalidSessionError(error)) {
          clear()
        } else {
          setSessionError('We couldn’t check your session. Check your connection and try again.')
        }
      })
      .finally(() => {
        if (check === sessionCheck.current) setIsHydrating(false)
      })
    return () => {
      sessionCheck.current += 1
    }
  }, [clear, sessionAttempt])

  const retrySession = useCallback(() => {
    if (!authTokens.access) {
      clear()
      return
    }
    setIsHydrating(true)
    setSessionError(null)
    setSessionAttempt((attempt) => attempt + 1)
  }, [clear])

  const login = useCallback(async ({ email, password }) => {
    const payload = await loginUser({ email, password })
    sessionCheck.current += 1
    authTokens.setTokens(payload.tokens.accessToken, payload.tokens.refreshToken)
    setUser(payload.user)
    setSessionError(null)
    setIsHydrating(false)
    persistUser(payload.user)
    return payload.user
  }, [])

  const logout = useCallback(async () => {
    if (authTokens.access) {
      try {
        await logoutUser()
      } catch {
        // best-effort; clear locally regardless
      }
    }
    clear()
  }, [clear])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === 'ADMIN',
      // Staff = anyone with an admin login (ADMIN, MANAGER, EMPLOYEE). This is
      // the first gate into /admin; which screens a role then gets is
      // `canAccessAdminRoute` in access.js, the same module this comes from.
      isStaff: isStaffRole(user?.role),
      role: user?.role ?? null,
      isHydrating,
      sessionError,
      retrySession,
      login,
      logout,
    }),
    [user, isHydrating, sessionError, retrySession, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
